import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, Subscription, combineLatest, Observable, throwError, Subject } from 'rxjs';
import { tap, filter, map } from 'rxjs/operators';

import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';

import { getSiteInfo } from '@shared/helpers/site-info';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';

import { ProductService } from 'projects/warehousing/src/app/services/product.service';
import { InvCountProductHandling } from 'projects/warehousing/src/app/models/inv-count-product-handling';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import { InventoryService } from 'projects/warehousing/src/app/services/inventory.service';
import { InvCountTpmInfo } from 'projects/warehousing/src/app/models/inv-count-tpm-info';
import { ShiftContext } from 'projects/warehousing/src/app/models/shift-context';
import { once } from '@shared/helpers/once';
import { saveDebounce } from '@shared/helpers/save-debounce';

type HandlingFormGroup = FormGroup<{
    id: FormControl<number>;
    displayOrder: FormControl<number>;
    productId: FormControl<number>;
    handledS1: FormControl<number>;
    handledS2: FormControl<number>;
    handledS3: FormControl<number>;
    handledS4: FormControl<number>;
    handledS5: FormControl<number>;

}>;

@Component({
    selector: 'app-walmart-misc-info',
    templateUrl: './walmart-misc-info.component.html',
    styleUrls: ['./walmart-misc-info.component.scss']
})
export class WalmartMiscInfoComponent implements OnInit, OnDestroy {
    readonly pageName = 'DailyProductionForm';
    readonly formDataName = 'WalmartMiscInfoProduction';

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    scrapTypes: SelectItem[] = [];
    rpcTypes: SelectItem[] = [];

    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();

    showShift: number = 0; // 1-5, 0 for all

    walmartMiscInfo: InvCountTpmInfo;
    walmartMiscInfoForm = new FormGroup({
        maintenanceOnlyRepairsS1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        maintenanceOnlyRepairsS2: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        maintenanceOnlyRepairsS3: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        maintenanceOnlyRepairsS4: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        maintenanceOnlyRepairsS5: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)])
    });

    siteSubinventoryId = new BehaviorSubject<number>(null);

    scrapItems: HandlingFormGroup[] = [];
    rpcSortedItems: HandlingFormGroup[] = [];

    scrapAddForm: HandlingFormGroup = new FormGroup({
        id: new FormControl<number>(null),
        displayOrder: new FormControl<number>(null),
        productId: new FormControl<number>(null, Validators.required),
        handledS1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS2: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS3: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS4: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS5: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)])
    });

    rpcSortedAddForm: HandlingFormGroup = new FormGroup({
        id: new FormControl<number>(null),
        displayOrder: new FormControl<number>(null),
        productId: new FormControl<number>(null, Validators.required),
        handledS1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS2: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS3: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS4: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS5: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)])
    });

    wwPalletsRepairedForm: HandlingFormGroup = new FormGroup({
        id: new FormControl<number>(null),
        displayOrder: new FormControl<number>(null),
        productId: new FormControl<number>(null, Validators.required),
        handledS1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS2: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS3: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS4: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)]),
        handledS5: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)])
    });

    context: ShiftContext;

    hasWWRepair: boolean = false;
    showTpmMaintenanceOnlyRepairs: boolean = false;

    readonly _itemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Item Deleted',
        detail: ''
    };

    readonly _tpmInfoUpdated: Message = {
        severity: 'success',
        summary: 'Misc Info Updated',
        detail: ''
    };

    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit(): void {
        const contextObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            const isTpm = getSiteInfo(context.site).isTpm;

            this.showTpmMaintenanceOnlyRepairs = isTpm;

            if (isTpm)
                this.showShift = 0;
            else
                this.showShift = context.shift.number;

            if (context.status !== ShiftStatus.Open) {
                this.walmartMiscInfoForm.disable({emitEvent: false});
                this.wwPalletsRepairedForm.disable({emitEvent: false});
                this.scrapAddForm.disable({emitEvent: false});
                this.rpcSortedAddForm.disable({emitEvent: false});
                this.scrapItems.forEach(f => f.disable({emitEvent: false}));
                this.rpcSortedItems.forEach(f => f.disable({emitEvent: false}));
            }
            else {
                this.walmartMiscInfoForm.enable({emitEvent: false});
                this.wwPalletsRepairedForm.enable({emitEvent: false});
                this.scrapAddForm.enable({emitEvent: false});
                this.rpcSortedAddForm.enable({emitEvent: false});
                this.scrapItems.forEach(f => f.enable({emitEvent: false}));
                this.rpcSortedItems.forEach(f => f.enable({emitEvent: false}));
            }
        }));

        const infoObservable = this.inventoryService.CurrentInvTpmInfo.pipe(
            filter(info => !!info),
            tap(info => {
                this.walmartMiscInfo = info;

                this.walmartMiscInfoForm.setValue({
                    maintenanceOnlyRepairsS1: info.maintenanceOnlyRepairsS1,
                    maintenanceOnlyRepairsS2: info.maintenanceOnlyRepairsS2,
                    maintenanceOnlyRepairsS3: info.maintenanceOnlyRepairsS3,
                    maintenanceOnlyRepairsS4: info.maintenanceOnlyRepairsS4,
                    maintenanceOnlyRepairsS5: info.maintenanceOnlyRepairsS5
                }, { emitEvent: false });
            }));

        const handlingObservable = combineLatest([
            this.productService.ProductsForSubinventory(this.siteSubinventoryId),
            this.inventoryService.CurrentInvCountContext.pipe(filter(c => !!c))
        ]).pipe(
            tap(([products, _invCount]) => {
                this.scrapTypes = products
                    .filter(x => x.itemNumber === 'Boxes'
                        || x.productType === 'IDC WW'
                        || x.productType === 'Airbag'
                        && !x.itemNumber.includes('Unsorted')
                    )
                    .map(p => ({ value: p.id, label: p.label }));
                this.rpcTypes = products
                    .filter(x => x.productType === 'RPC')
                    .map(p => ({ value: p.id, label: p.label }));
            }),
            once(([products, invCount]) => {
                const handling = invCount.invCountProductHandling;

                this.scrapItems = handling
                    .filter(h => this.scrapTypes.find(p => h.productId === p.value))
                    .map(h => this.createHandlingForm(h));
                this.rpcSortedItems = handling
                    .filter(h => this.rpcTypes.find(p => h.productId === p.value))
                    .map(h => this.createHandlingForm(h));

                const wwSortProduct = products.find(x => x.id === 100000141998956);
                if (wwSortProduct && invCount.id === this.invCountId && !this.hasWWRepair) {
                    // that last check prevents double-creation
                    this.hasWWRepair = true;

                    let wwRepairHandling = handling.find(h => h.productId === wwSortProduct.id);

                    if (!wwRepairHandling) {
                        wwRepairHandling = {
                            id: 0,
                            invCountId: this.invCountId,
                            productId: wwSortProduct.id,
                            displayOrder: 1,
                            handledS1: 0,
                            handledS2: 0,
                            handledS3: 0,
                            handledS4: 0,
                            handledS5: 0
                        };
                        this.inventoryService.createInvCountProductHandling(wwRepairHandling).subscribe(created => {
                            this.setHandlingForm(this.wwPalletsRepairedForm, created);
                        });
                    }
                    else {
                        this.setHandlingForm(this.wwPalletsRepairedForm, wwRepairHandling);
                    }
                }
            }));

        this.subscriptions = [
            this.walmartMiscInfoForm.valueChanges
                .pipe(saveDebounce(this.shutdown))
                .subscribe(_ => {
                    this.saveWalmartMiscInfo();
                }),
            this.wwPalletsRepairedForm.valueChanges
                .pipe(saveDebounce(this.shutdown))
                .subscribe(() => {
                    this.updateHandling(this.wwPalletsRepairedForm)
                }),
            combineLatest([contextObservable, handlingObservable, infoObservable])
                .pipe(this.activityService.Wait('TPM Misc Info'))
                .subscribe()
        ];
    }

    ngOnDestroy(): void {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.hasWWRepair = false;
        this.walmartMiscInfo = null;
        this.scrapItems = [];
        this.rpcSortedItems = [];
    }

    setHandlingForm(form: HandlingFormGroup, record: InvCountProductHandling): void {
        form.setValue({
            id: record.id,
            productId: record.productId,
            displayOrder: record.displayOrder,
            handledS1: record.handledS1,
            handledS2: record.handledS2,
            handledS3: record.handledS3,
            handledS4: record.handledS4,
            handledS5: record.handledS5
        }, { emitEvent: false });
    }

    createHandlingForm(record: InvCountProductHandling): HandlingFormGroup {
        const form = new FormGroup({
            id: new FormControl<number>(record.id),
            displayOrder: new FormControl<number>(record.displayOrder ?? 0),
            productId: new FormControl<number>(record.productId, Validators.required),
            handledS1: new FormControl<number>(record.handledS1, [Validators.required, CustomValidators.numeric(false)]),
            handledS2: new FormControl<number>(record.handledS2, [Validators.required, CustomValidators.numeric(false)]),
            handledS3: new FormControl<number>(record.handledS3, [Validators.required, CustomValidators.numeric(false)]),
            handledS4: new FormControl<number>(record.handledS4, [Validators.required, CustomValidators.numeric(false)]),
            handledS5: new FormControl<number>(record.handledS5, [Validators.required, CustomValidators.numeric(false)]),
        });

        if (this.context?.status !== ShiftStatus.Open)
            form.disable();
        else
            form.enable();

        form.valueChanges
            .pipe(saveDebounce(this.shutdown))
            .subscribe(_ => {
                this.updateHandling(form);
            });

        return form;
    }

    updateHandling(form: HandlingFormGroup): void {
        const item: InvCountProductHandling = {
            invCountId: this.invCountId,
            displayOrder: form.controls.displayOrder.value,
            id: form.controls.id.value,
            productId: form.controls.productId.value,
            handledS1: form.controls.handledS1.value,
            handledS2: form.controls.handledS2.value,
            handledS3: form.controls.handledS3.value,
            handledS4: form.controls.handledS4.value,
            handledS5: form.controls.handledS5.value
        };
        this.inventoryService.updateInvCountProductHandling(item).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Handling Item Updated',
                detail: ''
            });
        });
    }

    deleteHandling(item: HandlingFormGroup): void {
        this.inventoryService.deleteInvCountProductHandling(item.controls.id.value).subscribe(() => {
            this.scrapItems.filter(s => s.value.id !== item.value.id);
            this.rpcSortedItems = this.rpcSortedItems.filter(rsi => rsi.value.id !== item.value.id);

            this.messageService.add(this._itemDeletedMsg);
        });
    }

    addHandling(form: HandlingFormGroup, order: number, description: string): Observable<HandlingFormGroup> {
        if (!form.valid) {
            form.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: `Could not create ${description}`,
                detail: 'Fields are required.'
            });

            return throwError(() => 'Invalid Form');
        } else {
            const entry: InvCountProductHandling = {
                invCountId: this.invCountId,
                displayOrder: order,
                productId: form.controls.productId.value,
                handledS1: form.controls.handledS1.value,
                handledS2: form.controls.handledS2.value,
                handledS3: form.controls.handledS3.value,
                handledS4: form.controls.handledS4.value,
                handledS5: form.controls.handledS5.value
            };

            return this.inventoryService.createInvCountProductHandling(entry).pipe(map(created => {
                form.reset({
                    productId: null,
                    handledS1: 0,
                    handledS2: 0,
                    handledS3: 0,
                    handledS4: 0,
                    handledS5: 0
                });
                this.messageService.add({
                    severity: 'success',
                    summary: `${description} Created`
                });

                return this.createHandlingForm(created);
            }));
        }
    }

    createScrapItem(): void {
        this.addHandling(this.scrapAddForm, this.scrapItems.length, 'Scrap Item').subscribe({
            next: _ => {
                this.scrapItems = [...this.scrapItems, _];
            },
            error: _ => {}
        });
    }

    saveRSI(): void {
        this.addHandling(this.rpcSortedAddForm, this.rpcSortedItems.length, 'RPC Sort Item').subscribe({
            next: _ => {
                this.rpcSortedItems = [...this.rpcSortedItems, _];
            },
            error: _ => {}
        });
    }

    saveWalmartMiscInfo(): void {
        if (!this.walmartMiscInfo) {
            return;
        }

        if (!this.walmartMiscInfoForm.valid) {
            this.walmartMiscInfoForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update Misc. Info',
                detail: 'Fields are required.'
            });
        } else {
            this.walmartMiscInfo.maintenanceOnlyRepairsS1 = this.walmartMiscInfoForm.controls.maintenanceOnlyRepairsS1.value;
            this.walmartMiscInfo.maintenanceOnlyRepairsS2 = this.walmartMiscInfoForm.controls.maintenanceOnlyRepairsS2.value;
            this.walmartMiscInfo.maintenanceOnlyRepairsS3 = this.walmartMiscInfoForm.controls.maintenanceOnlyRepairsS3.value;
            this.walmartMiscInfo.maintenanceOnlyRepairsS4 = this.walmartMiscInfoForm.controls.maintenanceOnlyRepairsS4.value;
            this.walmartMiscInfo.maintenanceOnlyRepairsS5 = this.walmartMiscInfoForm.controls.maintenanceOnlyRepairsS5.value;

            this.inventoryService.updateInvCountTpmInfo(this.walmartMiscInfo).subscribe(_x => {
                this.messageService.add(this._tpmInfoUpdated);
            });
        }
    }

    rowTrackBy(_i: number, item: HandlingFormGroup): number {
        return item.controls.id.value;
    }
}
